import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, Bell, ChevronRight, Globe } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LivePriceStrip from "@/components/LivePriceStrip";
import ListingCard from "@/components/ListingCard";
import CurrencySwitcher from "@/components/CurrencySwitcher";
import { ListingCardSkeleton } from "@/components/Skeletons";

const CHIPS = ["All", "Gold", "Chrome", "Manganese", "Iron Ore", "Coal", "Copper", "Coffee", "Lithium"];

export default function Home() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [chip, setChip] = useState("All");

  const { data: prices, isLoading: pLoading, dataUpdatedAt: pricesTs } = useQuery({
    queryKey: ["prices"],
    queryFn: async () => (await api.get("/prices")).data.items,
    refetchInterval: 5000,
  });

  const { data: listings, isLoading: lLoading } = useQuery({
    queryKey: ["home-listings", chip],
    queryFn: async () =>
      (await api.get("/listings", { params: { commodity: chip, status: "available", limit: 8, sort: "recent" } })).data.items,
    refetchInterval: 15000,
  });

  const { data: notifCount } = useQuery({
    queryKey: ["notif-unread"],
    queryFn: async () => (await api.get("/notifications/unread-count")).data.count,
    refetchInterval: 5000,
    enabled: !!user,
  });

  const toggleFav = async (l) => {
    try {
      const { data } = await api.post(`/favorites/${l.id}`);
      toast.success(data.favorited ? "Saved to favorites" : "Removed from favorites");
      qc.setQueryData(["home-listings", chip], (old) =>
        old?.map((x) => (x.id === l.id ? { ...x, is_favorite: data.favorited } : x)));
    } catch { toast.error("Please log in"); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      {/* Header */}
      <div className="sticky top-0 z-30 glass px-4 pb-3 pt-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-dim">Welcome back{user?.name ? "," : ""}</p>
            <h1 className="font-display text-2xl font-extrabold leading-tight">{user?.name?.split(" ")[0] || "Trader"}</h1>
          </div>
          <div className="flex items-center gap-2">
            <CurrencySwitcher />
            <button data-testid="notif-bell" onClick={() => nav("/notifications")}
                    className="pressable glass relative flex h-10 w-10 items-center justify-center rounded-full">
              <Bell size={18} />
              {notifCount > 0 && (
                <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white" style={{ background: "var(--red)" }}>
                  {notifCount > 9 ? "9+" : notifCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Live prices */}
      <div className="mb-1 mt-4 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg" style={{ background: "rgba(245,158,11,0.15)" }}>
            <Zap size={16} className="accent" fill="currentColor" />
          </div>
          <h2 className="font-display text-lg font-bold">Live Prices</h2>
          <span className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: "rgba(52,208,127,0.15)", color: "var(--green)" }}>
            <span className="live-dot h-1.5 w-1.5 rounded-full" style={{ background: "var(--green)" }} /> LIVE
          </span>
          <LastUpdated ts={pricesTs} />
        </div>
        <button onClick={() => nav("/prices")} data-testid="view-all-prices" className="pressable flex items-center gap-0.5 text-sm font-bold accent">
          View all <ChevronRight size={16} />
        </button>
      </div>
      <div className="mt-3">
        <LivePriceStrip prices={prices || []} loading={pLoading} onOpen={(s) => nav(`/prices/${s}`)} />
      </div>

      {/* Category chips */}
      <div className="no-scrollbar mt-5 flex gap-2 overflow-x-auto px-4">
        {CHIPS.map((c) => (
          <button key={c} onClick={() => setChip(c)} data-active={chip === c} data-testid={`chip-${c}`}
                  className="chip pressable flex shrink-0 items-center gap-1.5 px-4 py-2 text-sm font-semibold">
            {c === "All" && <Globe size={15} />} {c}
          </button>
        ))}
      </div>

      {/* Recent listings */}
      <div className="mb-3 mt-6 flex items-center justify-between px-4">
        <h2 className="font-display text-lg font-bold">Recent Listings</h2>
        <button onClick={() => nav("/browse")} data-testid="see-all-listings" className="pressable flex items-center gap-0.5 text-sm font-bold accent">
          See all <ChevronRight size={16} />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3 px-4 md:grid-cols-3">
        {lLoading
          ? [0, 1, 2, 3].map((i) => <ListingCardSkeleton key={i} />)
          : (listings || []).map((l, i) => (
              <ListingCard key={l.id} listing={l} index={i} onOpen={() => nav(`/listing/${l.id}`)} onToggleFav={toggleFav} />
            ))}
      </div>
      {!lLoading && (listings || []).length === 0 && (
        <div className="px-4 py-16 text-center text-dim">
          <p className="font-semibold">No listings yet in {chip}</p>
          <p className="mt-1 text-sm">Be the first to post one!</p>
        </div>
      )}
    </motion.div>
  );
}

function LastUpdated({ ts }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 5000);
    return () => clearInterval(id);
  }, []);
  if (!ts) return null;
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  const label = s < 5 ? "just now" : s < 60 ? `${s}s ago` : `${Math.floor(s / 60)}m ago`;
  return <span className="text-[11px] font-medium text-dim" data-testid="last-updated">Updated {label}</span>;
}
